import { useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';

/**
 * AdminSecretsPage — `/admin/secrets`
 *
 * Manages third-party API credentials stored in Azure Key Vault.
 * Currently surfaces:
 *  - APIFY_TOKEN (Apify Instagram + Facebook scraping)
 *  - SOCIAVAULT_API_KEY (cheaper Instagram + Reddit alternative)
 *
 * Submitted values are PUT to `/api/mgmt/secrets/<NAME>` which writes
 * directly to Key Vault. We never read secret values back.
 */

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export default function AdminSecretsPage() {
  const [apifyToken, setApifyToken] = useState('');
  const [apifyStatus, setApifyStatus] = useState<SaveStatus>('idle');
  const [sociavaultKey, setSociavaultKey] = useState('');
  const [sociavaultStatus, setSociavaultStatus] = useState<SaveStatus>('idle');

  const saveApifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apifyToken.trim()) return;
    setApifyStatus('saving');
    try {
      await api.put('/api/mgmt/secrets/APIFY_TOKEN', { value: apifyToken });
      setApifyToken('');
      setApifyStatus('saved');
      setTimeout(() => setApifyStatus('idle'), 3000);
    } catch {
      setApifyStatus('error');
    }
  };

  const saveSociavaultKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sociavaultKey.trim()) return;
    setSociavaultStatus('saving');
    try {
      await api.put('/api/mgmt/secrets/SOCIAVAULT_API_KEY', { value: sociavaultKey });
      setSociavaultKey('');
      setSociavaultStatus('saved');
      setTimeout(() => setSociavaultStatus('idle'), 3000);
    } catch {
      setSociavaultStatus('error');
    }
  };

  return (
    <AdminLayout>
      <h1>Secrets</h1>

      <section className="mb-5">
        <h2 className="h4">Apify</h2>
        <form onSubmit={saveApifyToken}>
          <div className="mb-3">
            <label htmlFor="apify-token" className="form-label">Apify API token</label>
            <input
              id="apify-token"
              type="password"
              className="form-control mh-input-wide"
              placeholder="Enter new token to update Key Vault…"
              value={apifyToken}
              onChange={(e) => setApifyToken(e.target.value)}
              aria-describedby="apify-token-help"
            />
            <div id="apify-token-help" className="form-text">
              Obtain from{' '}
              <a href="https://console.apify.com/account/integrations" target="_blank" rel="noreferrer">
                Apify Console → Account → Integrations
              </a>
              . Saved directly to Azure Key Vault as <code>APIFY-TOKEN</code>.
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={!apifyToken.trim() || apifyStatus === 'saving'}>
            {apifyStatus === 'saving' ? 'Saving…' : 'Save to Key Vault'}
          </button>
          {apifyStatus === 'saved' && <span className="ms-3 text-success">✓ Saved</span>}
          {apifyStatus === 'error' && <span className="ms-3 text-danger">Failed to save — is Key Vault configured?</span>}
        </form>
      </section>

      <section>
        <h2 className="h4">SociaVault</h2>
        <form onSubmit={saveSociavaultKey}>
          <div className="mb-3">
            <label htmlFor="sociavault-key" className="form-label">SociaVault API key</label>
            <input
              id="sociavault-key"
              type="password"
              className="form-control mh-input-wide"
              placeholder="sk_live_…"
              value={sociavaultKey}
              onChange={(e) => setSociavaultKey(e.target.value)}
              aria-describedby="sociavault-key-help"
            />
            <div id="sociavault-key-help" className="form-text">
              Cheaper alternative to Apify for Instagram + Reddit scraping (~$0.0048/call). Obtain from{' '}
              <a href="https://sociavault.com/dashboard" target="_blank" rel="noopener noreferrer">SociaVault dashboard</a>
              {' '}(sign up at{' '}
              <a href="https://sociavault.com/signup" target="_blank" rel="noopener noreferrer">sociavault.com/signup</a>
              {' '}for 50 free credits). Saved to Key Vault as <code>SOCIAVAULT-API-KEY</code>. Enable usage via Scan cadence → "Use SociaVault for Instagram/Reddit".
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={!sociavaultKey.trim() || sociavaultStatus === 'saving'}>
            {sociavaultStatus === 'saving' ? 'Saving…' : 'Save to Key Vault'}
          </button>
          {sociavaultStatus === 'saved' && <span className="ms-3 text-success">✓ Saved</span>}
          {sociavaultStatus === 'error' && <span className="ms-3 text-danger">Failed to save — is Key Vault configured?</span>}
        </form>
      </section>
    </AdminLayout>
  );
}
